import type { DivExtensionClass, DivExtensionContext } from '@divkitframework/divkit/typings/common.d.ts';
import type { FunnelTranslations, TranslationResponse } from '@/modules/i18n';

const getKey = (params: object): string[] => {
  const value = params as { key?: string; keyAuto?: string };
  return [value.keyAuto, value.key].filter((key): key is string => Boolean(key));
};

/** Разметка переводов: `<1>…</1>` — границы стилевых сегментов (по одному дочернему text-узлу на сегмент). */
const NUMERIC_TAG = /<\d+>([\s\S]*?)<\/\d+>/g;
/** Inline-теги, которые разрешал WebQuiz (DOMPurify ALLOWED_TAGS); всё остальное выводится текстом. */
const ALLOWED_TAGS = new Set(['br', 'strong', 'i', 'b', 'em', 'span', 's']);
const TAG = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*\/?>/g;

export const splitIntoSegments = (input: string): string[] =>
  input.split(NUMERIC_TAG).filter((segment) => segment.trim().length > 0);

/** Безопасный рендер inline-HTML без innerHTML: текст — только через textContent, теги — по allowlist. */
export const renderInlineHtml = (target: Element, html: string): void => {
  const doc = target.ownerDocument;
  target.replaceChildren();
  const stack: Element[] = [target];
  let last = 0;
  for (const match of html.matchAll(TAG)) {
    const text = html.slice(last, match.index);
    if (text) stack[stack.length - 1].append(doc.createTextNode(text));
    last = match.index + match[0].length;
    const name = match[1].toLowerCase();
    const closing = match[0].startsWith('</');
    if (!ALLOWED_TAGS.has(name)) {
      stack[stack.length - 1].append(doc.createTextNode(match[0]));
      continue;
    }
    if (name === 'br') {
      if (!closing) stack[stack.length - 1].append(doc.createElement('br'));
      continue;
    }
    if (closing) {
      if (stack.length > 1) stack.pop();
      continue;
    }
    const element = doc.createElement(name);
    stack[stack.length - 1].append(element);
    stack.push(element);
  }
  const tail = html.slice(last);
  if (tail) stack[stack.length - 1].append(doc.createTextNode(tail));
};

/**
 * WebPaywalls convention kept for price cards: these keys are not locale entries but placeholders for
 * the tariff's CMS `translations`. A CMS value is normally a locale key (`fit.paywall.offer.week_trial`)
 * resolved client-side with the tariff as `priceListItem`; a value that is not a key is shown as text.
 */
export const TARIFF_TEXT_KEYS: Record<string, string> = {
  OFFER_KEY_REPLACE: 'offerTitle',
  TITLE_KEY_REPLACE: 'priceTitle',
  DESCRIPTION_KEY_REPLACE: 'priceDescription',
};

type TariffLike = {
  translations?: Record<string, unknown>;
  price?: {
    _id?: string;
    price?: number;
    interval?: string | null;
    intervalCount?: number | null;
    trial?: { price?: number; interval?: string | null; intervalCount?: number | null } | null;
  };
  priceDetails?: { originalPrice?: number; discount?: number } | null;
};

/**
 * Inline price list exposes the tariff as `it`, legacy strings say `priceListItem`, and outside the price
 * card the host publishes the selection as `selectedPrice` — so a key works anywhere on the paywall.
 */
const tariffFrom = (variables: Record<string, unknown>): TariffLike | undefined =>
  (variables.priceListItem ?? variables.it ?? variables.selectedPrice) as TariffLike | undefined;

/**
 * One place where the tariff gets its historical name: keys written for WebPaywalls address it as
 * `priceListItem`, and they must work both inside the price list (`it`) and anywhere else on the paywall
 * (`selectedPrice`).
 */
export const withTariffAliases = (variables: Record<string, unknown>): Record<string, unknown> => {
  const item = tariffFrom(variables);
  return item ? { ...variables, priceListItem: item } : variables;
};

/**
 * Flat values WebPaywalls passed to every tariff string (`<s>{{oldPrice}}</s> {{price}} per
 * {{intervalCount}} {{interval}}`), next to the object paths (`{{priceListItem.price.price, priceFormat}}`)
 * the DivKit paywalls used. Money arrives pre-formatted and `oldPrice` falls back to the price itself,
 * exactly as `PriceListItem` did.
 */
export const tariffValues = (
  item: TariffLike | undefined,
  formatPrice: (amount: number) => string,
): Record<string, unknown> => {
  const price = Number(item?.price?.price) || 0;
  const trial = item?.price?.trial;
  return {
    price: formatPrice(price),
    oldPrice: formatPrice(Number(item?.priceDetails?.originalPrice) || price),
    trialPrice: formatPrice(Number(trial?.price) || 0),
    trialInterval: trial?.interval ?? '',
    trialIntervalCount: trial?.intervalCount ?? 0,
    interval: item?.price?.interval ?? '',
    intervalCount: item?.price?.intervalCount ?? 0,
    discount: Number(item?.priceDetails?.discount) || 0,
  };
};

export interface TariffText {
  text: string;
  /** CMS field the key maps to; `missing` when the tariff has no text for it. */
  field: string;
  missing: boolean;
  priceId?: string;
}

/** Text for a tariff placeholder key from `it`/`priceListItem`; undefined when the key is not one. */
export const resolveTariffText = (keys: string[], variables: Record<string, unknown>): TariffText | undefined => {
  const key = keys.find((candidate) => candidate in TARIFF_TEXT_KEYS);
  if (!key) return undefined;
  const item = tariffFrom(variables);
  const field = TARIFF_TEXT_KEYS[key];
  const value = item?.translations?.[field];
  const text = typeof value === 'string' ? value : '';
  return { text, field, missing: text.trim() === '', priceId: item?.price?._id };
};

export interface LocalizationDiagnostics {
  /** Show `⚠ field` in place of an empty tariff text (debug builds). */
  markMissing?: boolean;
  onMissing?: (info: { keys: string[]; field?: string; priceId?: string }) => void;
}

/** CMS tariff text → locale entry when the value is a key, otherwise the literal text. */
export const translateTariffText = (
  translations: Pick<FunnelTranslations, 'translate'> & Partial<Pick<FunnelTranslations, 'formatPrice'>>,
  tariffText: TariffText,
  variables: Record<string, unknown>,
  diagnostics?: LocalizationDiagnostics,
): TranslationResponse => {
  if (tariffText.missing) {
    return {
      translation: diagnostics?.markMissing ? `\u26a0 ${tariffText.field}` : '',
      requestedLng: '',
      usedLng: '',
    };
  }
  const formatPrice = translations.formatPrice ?? ((amount: number) => String(amount));
  const values = { ...withTariffAliases(variables), ...tariffValues(tariffFrom(variables), formatPrice) };
  return (
    translations.translate([tariffText.text], values) ?? { translation: tariffText.text, requestedLng: '', usedLng: '' }
  );
};

class LocalizationExtension {
  private readonly keys: string[];
  private readonly reRenderOnVariable?: string;
  private listener?: () => void;
  private unsubscribes: Array<() => void> = [];
  private reported = false;

  constructor(params: object) {
    this.keys = getKey(params);
    const raw = params as { reRenderOnVariable?: unknown };
    if (typeof raw.reRenderOnVariable === 'string') this.reRenderOnVariable = raw.reRenderOnVariable;
  }

  mountView(node: HTMLElement, context: DivExtensionContext): void {
    const translations = (LocalizationExtension as unknown as { translations: FunnelTranslations }).translations;
    const render = () => {
      const variables = this.getContextVariables(context);
      const diagnostics = (LocalizationExtension as unknown as { diagnostics?: LocalizationDiagnostics }).diagnostics;
      const tariffText = resolveTariffText(this.keys, variables);
      if (tariffText?.missing && !this.reported) {
        this.reported = true;
        diagnostics?.onMissing?.({ keys: this.keys, field: tariffText.field, priceId: tariffText.priceId });
      }
      const result =
        tariffText !== undefined
          ? translateTariffText(translations, tariffText, variables, diagnostics)
          : translations.translate(this.keys, variables);
      if (!result) {
        // Locale has no such key: the layer text stays as the fallback — worth knowing in debug.
        if (!this.reported && this.keys.some((key) => !key.startsWith('__'))) {
          this.reported = true;
          diagnostics?.onMissing?.({ keys: this.keys });
        }
        return;
      }
      const container = node.firstElementChild ?? node;
      const segments = splitIntoSegments(result.translation);
      const children = Array.from(container.children);
      // Rich text из экспортера — контейнер с text-узлом на каждый <n>-сегмент: раздаём сегменты по детям.
      if (segments.length > 1 && children.length >= segments.length) {
        segments.forEach((segment, index) => renderInlineHtml(children[index], segment));
        return;
      }
      const target = children.length === 1 ? children[0] : container;
      renderInlineHtml(target, result.translation.replace(NUMERIC_TAG, '$1'));
    };

    render();
    this.listener = render;
    translations.addListener('translationLoaded', render);
    const watched = (LocalizationExtension as unknown as { watch?: string[] }).watch ?? [];
    // `reRenderOnVariable` comes from the exporter; `watch` is what the host keeps fresh (the selection).
    for (const name of [this.reRenderOnVariable, ...watched].filter(Boolean) as string[]) {
      const subscription = context.variables.get(name)?.subscribe?.(render);
      if (typeof subscription === 'function') this.unsubscribes.push(subscription);
    }
  }

  unmountView(): void {
    const translations = (LocalizationExtension as unknown as { translations: FunnelTranslations }).translations;
    if (this.listener) translations.removeListener('translationLoaded', this.listener);
    this.unsubscribes.splice(0).forEach((unsubscribe) => unsubscribe());
  }

  private getContextVariables(context: DivExtensionContext): Record<string, unknown> {
    const values = Array.from(context.variables).reduce<Record<string, unknown>>((acc, [name, variable]) => {
      acc[name] = variable.getValue();
      return acc;
    }, {});
    // Legacy strings say `priceListItem`: inside the list that is `it`, outside it is the host's selection.
    return withTariffAliases(values);
  }
}

/** Variables whose change re-renders every localized text: the selection drives price data in keys. */
export const WATCHED_VARIABLES = ['selectedPrice', 'selectedPriceId'];

export const createLocalizationExtensions = (
  translations: FunnelTranslations,
  diagnostics?: LocalizationDiagnostics,
  watch: string[] = WATCHED_VARIABLES,
) => {
  (LocalizationExtension as unknown as { translations: FunnelTranslations }).translations = translations;
  (LocalizationExtension as unknown as { diagnostics?: LocalizationDiagnostics }).diagnostics = diagnostics;
  (LocalizationExtension as unknown as { watch?: string[] }).watch = watch;
  return new Map<string, DivExtensionClass>([['localization', LocalizationExtension as DivExtensionClass]]);
};
